import { NewsletterRepository } from '@Engage.Data/repositories/newsletter/NewsletterRepository';
import { NewsletterStorage } from '@Engage.Data/services/storage/NewsletterStorage';
import { localStorageService } from '@Engage.Data/services/storage/localStorage/LocalStorageService';
import { UserStore } from '../../../../plugins/auth/model/UserStore';
export class NewsletterEngine {
    // ─── Already-subscribed check ─────────────────────────────────────────────
    // Mirrors Connext v2 SubscribePreference.isUserAlreadySubscribe():
    //   checks igm_epref cookie AND User.EmailPreferences (if logged in).
    static isUserAlreadySubscribed(preferences) {
        var _a;
        if (!(preferences === null || preferences === void 0 ? void 0 : preferences.length))
            return false;
        const templateIds = preferences.map(p => p.Id).filter(id => id > 0);
        if (!templateIds.length)
            return false;
        // 1. Cookie-stored IDs (anonymous + previously subscribed)
        const cookieIds = NewsletterStorage.getPreferenceIds();
        // 2. Logged-in user's server-side EmailPreferences
        let userIds = [];
        try {
            const prefs = (_a = UserStore.getUser()) === null || _a === void 0 ? void 0 : _a.EmailPreferences;
            if (Array.isArray(prefs)) {
                userIds = prefs.map((p) => { var _a; return (_a = p.Id) !== null && _a !== void 0 ? _a : p.EmailPreferenceId; }).filter(Boolean);
            }
        }
        catch ( /* no user */_b) { /* no user */ }
        const allStoredIds = Array.from(new Set([...cookieIds, ...userIds]));
        if (allStoredIds.length === 0)
            return false;
        // All template preference IDs must be present — mirrors Connext v2 difference() check
        return templateIds.every(id => allStoredIds.includes(id));
    }
    // ─── Fetch preferences by email ───────────────────────────────────────────
    static getEmailPreferences(email) {
        return NewsletterRepository.getEmailPreferences(email)
            .then((data) => {
            if (!Array.isArray(data))
                return [];
            return data
                .map((item) => {
                var _a, _b;
                const p = item.Preference || item;
                return {
                    Id: (_a = p.EmailPreferenceId) !== null && _a !== void 0 ? _a : p.Id,
                    Name: (_b = p.Title) !== null && _b !== void 0 ? _b : p.Name,
                    Description: p.Description,
                    SubscriberOnly: p.SubscriberOnly,
                };
            })
                .filter(p => p.Id > 0);
        });
    }
    // ─── Form binding ─────────────────────────────────────────────────────────
    // Mirrors Connext v2 NewsletterForm.submit() + SubscribeNewsletterSubStep.submitForm()
    static bindSubmit(action) {
        const subscribeSubstep = this.getSubscribeSubstep();
        if (!subscribeSubstep)
            return;
        const button = subscribeSubstep.querySelector("[data-nxt-button='subscribe']");
        if (!button)
            return;
        // Use AbortController so we can remove the listener on success (no double-fire)
        const ac = new AbortController();
        button.addEventListener('click', (e) => {
            e.preventDefault();
            const email = this.getEmailFromContainer(subscribeSubstep);
            const validationError = this.validateEmail(email);
            if (validationError) {
                this.showError(subscribeSubstep, validationError);
                return;
            }
            this.hideError(subscribeSubstep);
            this.setLoading(button, true);
            const preferences = this.getPreferences(action);
            if (!preferences.length) {
                this.showError(subscribeSubstep, 'Newsletter IDs cannot be null or empty.');
                this.setLoading(button, false);
                return;
            }
            this.subscribe(email, preferences, action)
                .then(() => {
                // Remove listener after success — mirrors Connext v2 one-shot Promise resolve
                ac.abort();
            })
                .catch((err) => {
                var _a;
                const message = (err === null || err === void 0 ? void 0 : err.ErrorMessage)
                    || (err === null || err === void 0 ? void 0 : err.message)
                    || ((_a = action === null || action === void 0 ? void 0 : action.showWhat) === null || _a === void 0 ? void 0 : _a.ErrorMessage)
                    || 'Something went wrong. Please try again.';
                this.showError(subscribeSubstep, message);
            })
                .finally(() => this.setLoading(button, false));
        }, { signal: ac.signal });
    }
    // ─── Subscribe ────────────────────────────────────────────────────────────
    // Mirrors Connext v2 NewsletterForm.sendData() → successSubmit/errorSubmit
    //   + SubscribeNewsletterSubStep: hide self → SuccessNewsletterSignUpSubStep.show()
    //   + Newsletter.successNewsletterSignUp(): merge cookie, set NW flag, reload if needed
    static subscribe(email, preferences, action) {
        const payload = {
            Email: email,
            EmailPreferences: preferences
                .filter(p => p.Id > 0 || p.EventName)
                .map(p => {
                const pref = { Id: p.Id };
                if (p.EventName)
                    pref.EventName = p.EventName;
                return pref;
            })
        };
        return NewsletterRepository.subscribe(payload)
            .then((response) => {
            var _a, _b;
            // 1. Merge preference IDs into cookie (mirrors mergeEmailPreferenceIdsToStorage)
            NewsletterStorage.mergePreferenceIds(preferences.map(p => p.Id).filter(id => id > 0));
            // 2. Set NW flag if configured (mirrors ClientModule.setNewsletterNW)
            if ((_a = action === null || action === void 0 ? void 0 : action.showWhat) === null || _a === void 0 ? void 0 : _a.IsSetStorageSignupSuccess) {
                NewsletterStorage.setNewsletterNW(true);
            }
            // 3. Show the correct substep
            //    Mirrors: SubscribeNewsletterSubStep.hide() → SuccessNewsletterSignUpSubStep.show()
            if ((response === null || response === void 0 ? void 0 : response.AlreadySubscribed) || (response === null || response === void 0 ? void 0 : response.IsAlreadySubscribed)) {
                this.showSubstep('AlreadySubscribedNewsletter');
            }
            else {
                this.showSubstep('SuccessNewsletterSignUp');
            }
            // 4. Dispatch event
            this.dispatchEvent('NewsletterSignUp', payload);
            // 5. Reload if configured (mirrors isNeedRerunConnext)
            if ((_b = action === null || action === void 0 ? void 0 : action.showWhat) === null || _b === void 0 ? void 0 : _b.IsConnextReRun) {
                setTimeout(() => window.location.reload(), 1000);
            }
        })
            .catch((error) => {
            var _a;
            this.dispatchEvent('NewsletterSignUpFails', { payload, error });
            const errorMessage = (error === null || error === void 0 ? void 0 : error.ErrorMessage)
                || (error === null || error === void 0 ? void 0 : error.message)
                || ((_a = action === null || action === void 0 ? void 0 : action.showWhat) === null || _a === void 0 ? void 0 : _a.ErrorMessage)
                || 'Something went wrong. Please try again.';
            // Try a dedicated ErrorNewsletterSignUp substep first
            const errSubstep = document.querySelector("[data-nxt-template-substep='ErrorNewsletterSignUp']");
            if (errSubstep) {
                this.showSubstep('ErrorNewsletterSignUp');
                this.injectMessage(errSubstep, errorMessage);
            }
            else {
                // No substep — surface to bindSubmit's catch to show inline error
                return Promise.reject({ ...error, ErrorMessage: errorMessage });
            }
        });
    }
    // ─── Email autofill ───────────────────────────────────────────────────────
    static autofillEmail(container) {
        var _a;
        const input = container.querySelector("[data-nxt-input='Email'], .connext-email-input, [data-mg2-input='Email'], input[type='email']");
        if (!input)
            return;
        try {
            const email = (_a = UserStore.getUser()) === null || _a === void 0 ? void 0 : _a.Email;
            if (email) {
                input.value = email;
                return;
            }
        }
        catch ( /* no user */_b) { /* no user */ }
        const fallback = localStorageService.get('UserEmail', { expandKey: false });
        if (fallback)
            input.value = fallback;
    }
    // ─── Preferences resolution ───────────────────────────────────────────────
    // Priority order mirrors Connext v2:
    //   1. showWhat.EmailPreferences (full objects)
    //   2. showWhat.Newsletter (numeric IDs, comma-separated)
    //      EventName from data-tmpl-action-value on the Newsletter span
    //   3. JSON in Newsletter span textContent (legacy)
    static getPreferences(action) {
        var _a, _b, _c, _d;
        // 1. Full objects from API
        const fromAction = ((_a = action === null || action === void 0 ? void 0 : action.showWhat) === null || _a === void 0 ? void 0 : _a.EmailPreferences) || [];
        if (fromAction.length)
            return fromAction;
        const span = this.getNewsletterSpan((_b = action === null || action === void 0 ? void 0 : action.html) !== null && _b !== void 0 ? _b : '');
        // 2. Numeric IDs from showWhat.Newsletter
        const newsletterId = (_c = action === null || action === void 0 ? void 0 : action.showWhat) === null || _c === void 0 ? void 0 : _c.Newsletter;
        if (newsletterId) {
            const eventName = (span === null || span === void 0 ? void 0 : span.getAttribute('data-tmpl-action-value')) || undefined;
            const ids = this.parseNewsletterIds(newsletterId);
            if (ids.length) {
                return ids.map(id => ({ Id: id, Name: '', EventName: eventName }));
            }
        }
        // 3. JSON in span textContent
        if (span) {
            const eventName = span.getAttribute('data-tmpl-action-value') || undefined;
            const text = (_d = span.textContent) === null || _d === void 0 ? void 0 : _d.trim();
            if (text) {
                try {
                    const parsed = JSON.parse(decodeURIComponent(text));
                    if (Array.isArray(parsed) && parsed.length) {
                        return parsed
                            .map((p) => {
                            var _a, _b, _c, _d, _e;
                            return ({
                                Id: parseInt((_a = p.id) !== null && _a !== void 0 ? _a : p.Id, 10),
                                Name: (_b = p.name) !== null && _b !== void 0 ? _b : p.Name,
                                Description: (_c = p.description) !== null && _c !== void 0 ? _c : p.Description,
                                SubscriberOnly: p.SubscriberOnly,
                                EventName: (_e = (_d = p.eventName) !== null && _d !== void 0 ? _d : p.EventName) !== null && _e !== void 0 ? _e : eventName
                            });
                        })
                            .filter(p => !isNaN(p.Id) && p.Id > 0);
                    }
                }
                catch ( /* fall through */_e) { /* fall through */ }
            }
            // Span with only eventName (no JSON body)
            if (eventName) {
                return [{ Id: 0, Name: eventName, EventName: eventName }];
            }
        }
        return [];
    }
    // ─── Substep navigation ───────────────────────────────────────────────────
    // Mirrors Connext v2:
    //   SubscribeNewsletterSubStep.hide()  → hides [data-nxt-template-substep='SubscribeNewsletter']
    //   SuccessNewsletterSignUpSubStep.show() → shows [data-nxt-template-substep='SuccessNewsletterSignUp']
    // The Newsletter step wrapper stays visible throughout.
    static showSubstep(substepName) {
        const target = document.querySelector(`[data-nxt-template-substep='${substepName}']`);
        if (!target)
            return false;
        // Hide the subscribe substep (not the whole step)
        const subscribeSubstep = document.querySelector("[data-nxt-template-substep='SubscribeNewsletter']");
        if (subscribeSubstep)
            subscribeSubstep.style.display = 'none';
        target.style.display = '';
        return true;
    }
    // ─── Private helpers ──────────────────────────────────────────────────────
    static getSubscribeSubstep() {
        var _a, _b;
        return (_b = (_a = document.querySelector("[data-nxt-template-substep='SubscribeNewsletter']")) !== null && _a !== void 0 ? _a : document.querySelector("[data-nxt-template-step='Newsletter']")) !== null && _b !== void 0 ? _b : document.body;
    }
    static getEmailFromContainer(container) {
        var _a, _b;
        const input = container.querySelector("[data-nxt-input='Email'], .connext-email-input, [data-mg2-input='Email'], input[type='email']");
        return (_b = (_a = input === null || input === void 0 ? void 0 : input.value) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : '';
    }
    static validateEmail(email) {
        if (!email)
            return 'Email is required.';
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
            ? null
            : 'Please enter a valid email address.';
    }
    // Mirrors Connext v2 Form.showServerErrors → [data-nxt-form-error]
    static showError(container, message) {
        var _a;
        const el = (_a = container.querySelector('[data-nxt-form-error]')) !== null && _a !== void 0 ? _a : container.querySelector('.connext-error-message');
        if (el) {
            el.textContent = message;
            el.style.display = 'block';
            return;
        }
        // Fallback: create a transient error element
        let fallback = container.querySelector('[data-nxt-newsletter-error]');
        if (!fallback) {
            fallback = document.createElement('div');
            fallback.setAttribute('data-nxt-newsletter-error', '');
            fallback.style.cssText = 'color:red;font-size:0.85em;margin-top:4px;';
            const input = container.querySelector("[data-nxt-input='Email'], input[type='email']");
            input === null || input === void 0 ? void 0 : input.insertAdjacentElement('afterend', fallback);
        }
        fallback.textContent = message;
        fallback.style.display = 'block';
    }
    // Mirrors Connext v2 Form.hideServerErrors
    static hideError(container) {
        const selectors = ['[data-nxt-form-error]', '.connext-error-message', '[data-nxt-newsletter-error]'];
        for (const sel of selectors) {
            const el = container.querySelector(sel);
            if (el) {
                el.textContent = '';
                el.style.display = 'none';
            }
        }
    }
    static setLoading(button, loading) {
        button.style.opacity = loading ? '0.6' : '1';
        button.style.pointerEvents = loading ? 'none' : '';
        button.setAttribute('aria-busy', String(loading));
    }
    static injectMessage(container, message) {
        const selectors = [
            '[data-tmpl-action-name="Success message"]',
            '[data-tmpl-action-name="Error message"]',
            '[data-nxt-form-error]',
            '.connext-error-message',
            '.connext-success-message',
        ];
        for (const sel of selectors) {
            const el = container.querySelector(sel);
            if (el) {
                el.textContent = message;
                el.style.display = 'block';
                return;
            }
        }
    }
    static parseNewsletterIds(value) {
        if (!value)
            return [];
        return String(value)
            .split(',')
            .map(id => parseInt(id.trim(), 10))
            .filter(id => !isNaN(id) && id > 0);
    }
    static getNewsletterSpan(html) {
        if (!html)
            return null;
        try {
            const wrapper = document.createElement('div');
            wrapper.innerHTML = html;
            return wrapper.querySelector("[data-tmpl-action-name='Newsletter']");
        }
        catch (_a) {
            return null;
        }
    }
    static dispatchEvent(name, detail) {
        document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
    }
}
